/**
 * Shared plumbing between a list page's URL state and its API request.
 *
 * Two things every list needs and each one used to do by hand — with the
 * predictable result that a filter could be wired into the query key (so the
 * screen refetched) but forgotten in the params (so the request went out
 * unchanged, and the filter looked alive while doing nothing).
 */
import { api } from '@/lib/api';
import { notify } from '@/components/ui/toaster';
import type { SortState } from '@/hooks/use-url-state';

/** Values a list filter can hold before it becomes a query param. */
export type FilterValue = string | number | boolean | undefined | null;

/**
 * Drop everything the API should not see.
 *
 * Empty string is the "unset" value throughout the URL-state layer, and sending
 * `?role=` is not the same request as sending nothing — the backend's enum
 * refuses the empty string with a 422.
 */
export function build_params(
  filters: Record<string, FilterValue>,
  sort?: SortState,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params[key] = value;
  }
  if (sort) {
    // The URL carries one shareable `sort=field:dir` token; the API takes the
    // two params separately.
    params.sort_by = sort.by;
    params.sort_dir = sort.dir;
  }
  return params;
}

/**
 * Download a server-side CSV export.
 *
 * Goes through the axios client rather than an `<a href>` so the request
 * carries the bearer token, and so a failure surfaces as a toast instead of
 * navigating the console to a JSON error page.
 */
export async function download_export(
  path: string,
  params: Record<string, string | number | boolean>,
  fallback_filename: string,
): Promise<void> {
  try {
    const res = await api.get<Blob>(path, { params, responseType: 'blob' });

    // Prefer the server's filename — it is already dated and singular.
    const disposition = String(res.headers['content-disposition'] ?? '');
    const match = /filename="?([^";]+)"?/.exec(disposition);
    const filename = match?.[1] ?? fallback_filename;

    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    // The cap being hit is the one thing an operator must not discover later,
    // halfway through reconciling a month of payments.
    const truncated = String(res.headers['x-export-truncated'] ?? '') === 'true';
    const total = res.headers['x-export-total'];
    if (truncated) {
      notify.warning('Export truncated', {
        description: `${filename} holds the first 10,000 rows of ${total ?? 'the'} matching records. Narrow the filters to export the rest.`,
      });
    } else {
      notify.success('Export ready', {
        description: `${filename}${total ? ` · ${Number(total).toLocaleString('en-IN')} rows` : ''}`,
      });
    }
  } catch (err) {
    notify.error(err, 'The export failed.');
  }
}
